import { Observable, interval, Subject } from 'rxjs'
import { switchMap, takeUntil } from 'rxjs/operators'
import { getUserNotifications } from '@/services/notification'
import { INotification } from '@/types/notification'
import NotificationEventType from '@/enums/NotificationEventType'
import useStore from '@/state/store'
import { IStore } from '@/state/store'
import constants from '@/config/constants'
import { Identifier } from '@/state/signify'
import { isEmpty } from 'lodash'
import { getAidDetail } from './signify'
import { AidDto, type CreateMyNativeAidRequest } from '@/api/origin-agent-svc'
import DefaultIdentifierIntendedRole from '@/enums/origin-identifier-type'
import {
  findAllNativeAidsFromAgent,
  saveIdentifierToAgent
} from './wallet/agent'
import { userService, utilAgentService } from '@/utils/apis'
import { UserInfoDto } from '@/api/origin-user-mgmt-svc'
import { captureErrorWithScope } from '@/shared/error-capture'

type StoreSlice = Pick<
  IStore,
  | 'authentication'
  | 'signifyClient'
  | 'getIdentifiers'
  | 'createIdentifier'
  | 'addNotificationProcess'
  | 'removeNotificationProcess'
>

/**
 * Processes user-facing notifications from the Origin backend.
 *
 * **Singleton with live-store semantics (production):** When called without an
 * `injectedStore`, every internal `this.store` access calls `useStore.getState()`
 * at that moment so it always sees the current `signifyClient`, `authentication`,
 * etc. — never a stale snapshot from app startup.
 *
 * **Test injection:** Pass `injectedStore` to pin the store to a controlled
 * snapshot. Call `NotificationProcessor.resetForTest()` in `beforeEach` to
 * clear the singleton between tests.
 *
 * See `cd-0005` for the long-term plan to retire the singleton in favour of
 * per-component factory injection.
 */
class NotificationProcessor {
  private static instance: NotificationProcessor | null = null

  private pollInterval: number
  private stopPollingSubject: Subject<void>
  private notificationsSubject: Subject<any>
  private polling = false

  private constructor(
    pollInterval: number,
    private readonly _storeAccessor: () => StoreSlice
  ) {
    this.pollInterval = pollInterval
    this.stopPollingSubject = new Subject<void>()
    this.notificationsSubject = new Subject<any>()
  }

  private get store(): StoreSlice {
    return this._storeAccessor()
  }

  static getInstance(pollInterval: number, injectedStore?: StoreSlice) {
    if (!NotificationProcessor.instance) {
      const accessor = injectedStore
        ? () => injectedStore
        : () => useStore.getState()
      NotificationProcessor.instance = new NotificationProcessor(
        pollInterval,
        accessor
      )
    } else if (injectedStore) {
      console.warn(
        'NotificationProcessor: first-call wins — ignoring injectedStore on subsequent call'
      )
    }
    return NotificationProcessor.instance
  }

  /** Test seam — production code must never call this. */
  static resetForTest(): void {
    ;(NotificationProcessor as any).instance = undefined
  }

  /**
   * Test seam — returns whether the singleton is currently polling.
   * Asserts the invariant ("exactly one Subscription active") rather than
   * symptom-level call counts (BUG-0017 / BUG-0030).
   * Production code must never call this.
   */
  static isPolling(): boolean {
    return NotificationProcessor.instance?.polling ?? false
  }

  async processOnce(): Promise<any> {
    let { userInfo } = this.store.authentication
    if (!userInfo?.id) return

    try {
      const notifications = await getUserNotifications()
      // @ts-expect-error: cd-0026 — swagger-generated getUserNotifications return type mismatches processNotifications param (TS2345); BE-side swagger fix pending
      return this.processNotifications(notifications)
    } catch (ex) {
      console.error('NOTIFICATION_FETCH_ERROR :: ', ex)
      throw ex
    }
  }

  start(): void {
    if (this.polling) return
    this.polling = true
    interval(this.pollInterval)
      .pipe(
        switchMap(async () => {
          try {
            return await this.processOnce()
          } catch (e) {
            // processOnce already logged; report to Sentry so polling
            // failures don't disappear silently in production.
            const err = e instanceof Error ? e : new Error(String(e))
            captureErrorWithScope((scope) => {
              scope.setTag('source', 'notificationProcessor.start')
            }, err)
          }
        }),
        takeUntil(this.stopPollingSubject)
      )
      .subscribe()
  }

  stop(): void {
    this.polling = false
    this.stopPollingSubject.next()
  }

  subscribeToNotifications(): Observable<any> {
    return this.notificationsSubject.asObservable()
  }

  private processNotifications(
    notifications: INotification[]
  ): Observable<void> {
    const seen = new Set<string>()
    notifications.forEach((notification) => {
      if (seen.has(notification.id)) {
        // BUG-0021 / cd-0013: defensive per-cycle dedup-by-id; backend uniqueness is
        // not guaranteed and downstream handlers (AUTO_TODO → AID/DB writes) are not
        // fully idempotent.
        return
      }
      seen.add(notification.id)

      if (notification.channel?.toUpperCase() == 'AUTO_TODO') {
        this.handleAutoTodoNotification(notification)
      } else {
        this.handleUINotification(notification)
      }
      // this.notificationsSubject.next(notification)
    })

    return new Observable<void>((observer) => {
      observer.next()
      observer.complete()
    })
  }

  private async handleAutoTodoNotification(
    notification: INotification
  ): Promise<any> {
    let addedProcess: string | null = null
    try {
      console.info(
        `Processing auto-todo of type ${notification.description} id: ${notification.id}`
      )
      const userAids: Identifier[] = await this.store.getIdentifiers()
      const eventType = notification.description.toUpperCase()
      const witnesses = await userService.witnesses()
      // WitnessDto.prefix is generated as optional; filter out witnesses
      // missing a prefix so the resulting list is a concrete string[].
      const witnessAids: string[] =
        witnesses
          ?.map((w) => w.prefix)
          .filter((p): p is string => typeof p === 'string') ?? []
      const userInfo = this.store.authentication.userInfo
      // Auto-todo notifications can only fire for an authenticated session
      // with at least one org context. Bail rather than dereferencing a
      // nullable userInfo / optional orgs[0].
      if (!userInfo || !userInfo.id || !userInfo.orgs?.[0]?.id) {
        return
      }
      const agentSvcIdentifiers = await findAllNativeAidsFromAgent({
        userId: userInfo.id,
        orgId: userInfo.orgs[0].id
      })
      switch (eventType) {
        case NotificationEventType.AUTO_TODO_CREATE_DAR_AID.toUpperCase():
          addedProcess =
            NotificationEventType.AUTO_TODO_CREATE_DAR_AID.toUpperCase()
          this.store.addNotificationProcess(addedProcess)
          // Re-narrow against the latest store state: outer userInfo was
          // captured at function entry and might be stale by the time we hit
          // this case. UserInfoDto.id / firstName / lastName are generated as
          // optional; bail if any are missing or signifyClient is null.
          const darUser = this.store.authentication.userInfo
          const darOrg = darUser?.orgs?.[0]
          if (
            !darUser ||
            isEmpty(darUser.firstName) ||
            isEmpty(darUser.lastName) ||
            !darUser.id ||
            !darOrg?.id ||
            !this.store.signifyClient
          )
            break
          const org = darOrg

          const darAlias = (
            await utilAgentService.constructAlias({
              user: `${darUser.firstName} ${darUser.lastName}`,
              role: constants.ROLES.DAR,
              org: org?.name
            })
          )?.alias

          const agentSvcDarIdentifier = agentSvcIdentifiers.find(
            (i) =>
              i.type === DefaultIdentifierIntendedRole.DAR &&
              i.orgId === org?.id
          )

          if (!agentSvcDarIdentifier && darAlias) {
            console.log('Creating Dar Aid in Keria Wallet')
            let newAid = await this.store.createIdentifier(
              darAlias,
              witnessAids
            )
            if (newAid) {
              let aidDetail = await getAidDetail(
                this.store.signifyClient,
                darAlias,
                'agent'
              )
              console.log('Storing DAR AID in Agent svc')
              await this.storeAidInDb(
                aidDetail,
                darUser.id,
                // darOrg.id is provably defined by the guard above
                // (`!darOrg?.id` breaks out); TS narrowing doesn't carry
                // from optional chain to direct access, hence the `!`.
                darOrg.id!,
                DefaultIdentifierIntendedRole.DAR
              )
            }
          }

          this.store.removeNotificationProcess(
            NotificationEventType.AUTO_TODO_CREATE_DAR_AID.toUpperCase()
          )

          break
        case NotificationEventType.AUTO_TODO_CREATE_LAR_AID.toUpperCase():
          addedProcess =
            NotificationEventType.AUTO_TODO_CREATE_LAR_AID.toUpperCase()
          this.store.addNotificationProcess(addedProcess)
          const lar = this.store.authentication.userInfo
          const larOrg = lar?.orgs?.[0]
          if (
            !lar ||
            isEmpty(lar.firstName) ||
            isEmpty(lar.lastName) ||
            !lar.id ||
            !larOrg?.id ||
            !this.store.signifyClient
          )
            break

          const larAlias = (
            await utilAgentService.constructAlias({
              user: `${lar.firstName} ${lar.lastName}`,
              role: constants.ROLES.LAR,
              org: larOrg.name
            })
          )?.alias

          const agentSvcLarIdentifier = agentSvcIdentifiers.find(
            (i) =>
              i.type === DefaultIdentifierIntendedRole.LAR &&
              i.orgId === larOrg.id
          )

          if (!agentSvcLarIdentifier && larAlias) {
            console.log('Creating Lar Aid in Keria Wallet')
            let newAid = await this.store.createIdentifier(
              larAlias,
              witnessAids
            )
            if (newAid) {
              let aidDetail = await getAidDetail(
                this.store.signifyClient,
                larAlias,
                'agent'
              )
              console.log('Storing LAR AID in Agent svc')
              await this.storeAidInDb(
                aidDetail,
                lar.id,
                larOrg.id,
                DefaultIdentifierIntendedRole.LAR
              )
            }
          }

          this.store.removeNotificationProcess(
            NotificationEventType.AUTO_TODO_CREATE_LAR_AID.toUpperCase()
          )

          break
        case NotificationEventType.AUTO_TODO_USER_ONBOARDING_COMPLETE.toUpperCase():
          addedProcess =
            NotificationEventType.AUTO_TODO_USER_ONBOARDING_COMPLETE.toUpperCase()
          this.store.addNotificationProcess(addedProcess)
          const originUser = this.store.authentication.userInfo
          const originOrg = originUser?.orgs?.[0]
          if (
            !originUser ||
            isEmpty(originUser.firstName) ||
            isEmpty(originUser.lastName) ||
            !originUser.id ||
            !originOrg?.id ||
            !this.store.signifyClient
          )
            break

          if (originUser.grants && originUser.grants.length > 0) {
            let hasEmpolyeeRole = originUser.grants.some(
              (x) => x.toUpperCase() === constants.ROLES.EMPLOYEE.toUpperCase()
            )
            if (hasEmpolyeeRole == true) {
              const roleAlias = (
                await utilAgentService.constructAlias({
                  user: `${originUser.firstName} ${originUser.lastName}`,
                  role: constants.ROLES.EMPLOYEE,
                  org: originOrg.name
                })
              )?.alias

              const agentSvcEmployeeIdentifier = agentSvcIdentifiers.find(
                (i) =>
                  i.type === DefaultIdentifierIntendedRole.EMPLOYEE &&
                  i.orgId === originOrg.id
              )

              if (!agentSvcEmployeeIdentifier && roleAlias) {
                let newAid = await this.store.createIdentifier(
                  roleAlias,
                  witnessAids
                )
                if (newAid) {
                  let aidDetail = await getAidDetail(
                    this.store.signifyClient,
                    roleAlias,
                    'agent'
                  )
                  console.log('Storing employee AID in Agent svc')
                  await this.storeAidInDb(
                    aidDetail,
                    originUser.id,
                    originOrg.id,
                    DefaultIdentifierIntendedRole.EMPLOYEE
                  )
                }
              }
            }
          }

          this.store.removeNotificationProcess(
            NotificationEventType.AUTO_TODO_USER_ONBOARDING_COMPLETE.toUpperCase()
          )
          break
        case NotificationEventType.AUTO_TODO_CREATE_OOR_TITLE_AID.toUpperCase():
          addedProcess =
            NotificationEventType.AUTO_TODO_CREATE_OOR_TITLE_AID.toUpperCase()
          this.store.addNotificationProcess(addedProcess)
          const oorUser = this.store.authentication.userInfo
          if (
            !oorUser ||
            isEmpty(oorUser.firstName) ||
            isEmpty(oorUser.lastName)
          )
            break
          await this.createRoleTitleAid(
            oorUser,
            witnessAids,
            agentSvcIdentifiers
          )

          this.store.removeNotificationProcess(
            NotificationEventType.AUTO_TODO_CREATE_OOR_TITLE_AID.toUpperCase()
          )
          break
        case NotificationEventType.AUTO_TODO_CREATE_ECR_TITLE_AID.toUpperCase():
          addedProcess =
            NotificationEventType.AUTO_TODO_CREATE_ECR_TITLE_AID.toUpperCase()
          this.store.addNotificationProcess(addedProcess)
          const ecrUser = this.store.authentication.userInfo
          if (
            !ecrUser ||
            isEmpty(ecrUser.firstName) ||
            isEmpty(ecrUser.lastName)
          )
            break
          await this.createRoleTitleAid(
            ecrUser,
            witnessAids,
            agentSvcIdentifiers
          )

          this.store.removeNotificationProcess(
            NotificationEventType.AUTO_TODO_CREATE_ECR_TITLE_AID.toUpperCase()
          )
          break
        default:
          console.warn(
            'Unknown notification auto-todo type:',
            eventType,
            notification
          )
      }
    } catch (e) {
      console.error('AUTO_TODO_HANDLER_ERROR :: ', e)
      // Report to Sentry so auto-todo failures don't disappear silently in production.
      const err = e instanceof Error ? e : new Error(String(e))
      captureErrorWithScope((scope) => {
        scope.setTag(
          'source',
          'notificationProcessor.handleAutoTodoNotification'
        )
      }, err)
    } finally {
      // Guarantee cleanup regardless of early break (guard condition) or thrown exception.
      // Without this, addNotificationProcess leaks into notificationProcessingStatus
      // forever, permanently blocking confirm-ceremony-identifier in AID_CREATING state.
      if (addedProcess !== null) {
        this.store.removeNotificationProcess(addedProcess)
      }
    }
  }

  private handleUINotification(notification: INotification): void {
    // console.log('handleUINotification : ' + JSON.stringify(notification))
    //TODO: implement UI notification logic and show to user
  }

  private async createRoleTitleAid(
    userInfo: UserInfoDto,
    witnessAids: string[],
    agentSvcIdentifiers: AidDto[]
  ) {
    try {
      // UserInfoDto.grants / orgs / id are generated as optional; the caller
      // already verified firstName/lastName, but those fields need their own
      // guards. Bail cleanly rather than crashing inside Promise.all.
      const userOrg = userInfo.orgs?.[0]
      const signifyClient = this.store.signifyClient
      if (!userInfo.grants || !userOrg?.id || !userInfo.id || !signifyClient) {
        return
      }
      const userOrgId = userOrg.id
      const userId = userInfo.id
      await Promise.all(
        userInfo.grants.map(async (grant) => {
          let title = grant
            .toLowerCase()
            .split(' ')
            .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
            .join('-')

          const titleAlias = (
            await utilAgentService.constructAlias({
              user: `${userInfo.firstName} ${userInfo.lastName}`,
              role: title,
              org: userOrg.name
            })
          )?.alias

          const agentSvcRoleIdentifier = agentSvcIdentifiers.find(
            (i) => i.type === title.toLowerCase() && i.orgId === userOrgId
          )

          if (!agentSvcRoleIdentifier && titleAlias) {
            let newAid = await this.store.createIdentifier(
              titleAlias,
              witnessAids
            )

            if (newAid) {
              let aidDetail = await getAidDetail(
                signifyClient,
                titleAlias,
                'agent'
              )
              console.log('Storing ROLE AID in Agent svc')
              await this.storeAidInDb(
                aidDetail,
                userId,
                userOrgId,
                title.toLowerCase()
              )
            }
          }
        })
      )
    } catch (e) {
      console.error('ERROR_IN_CREATING_ROLE_TITLE_IDENTIFIER :: ', e)
    }
  }

  private async storeAidInDb(
    aidDetail: {
      aid: any
      alias: any
      oobi: string[]
      isMultisig: boolean
    },
    userId: string,
    orgId: string,
    aidType: string
  ): Promise<boolean> {
    const payload: CreateMyNativeAidRequest = {
      ...aidDetail,
      type: aidType,
      orgId: orgId
    }
    await saveIdentifierToAgent(payload)
    return true
  }
}

export default NotificationProcessor
